import React, { useState, useEffect } from 'react';
import { Settings, Info, Shield, Database, Server, AlertCircle, Check, Save } from 'lucide-react';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    appName: 'Wellum PDF Viewer',
    companyName: 'Wellum Zrt.',
    maxFileSize: '50', // MB
    allowedFileTypes: '.pdf',
    sessionTimeout: '60', // minutes
    maxLoginAttempts: '5',
    enableDebugMode: false,
    autoBackup: true,
    backupInterval: '24', // hours
    maintenanceMode: false
  });
  
  const [systemInfo, setSystemInfo] = useState({
    version: '1.0.0',
    nodeVersion: '',
    uptime: '',
    memoryUsage: '',
    diskUsage: '',
    lastBackup: '',
    totalUsers: 0,
    totalFiles: 0,
    totalComments: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadSettings();
    loadSystemInfo();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('/api/system/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadSystemInfo = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/system/info', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSystemInfo(data.info);
      }
    } catch (error) {
      console.error('Error loading system info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      
      const response = await fetch('/api/system/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ settings })
      });
      
      if (response.ok) {
        setSuccess('Beállítások sikeresen mentve');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Hiba történt a beállítások mentése során');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Hiba történt a beállítások mentése során');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCheckboxChange = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatUptime = (uptime) => {
    const days = Math.floor(uptime / (24 * 60 * 60));
    const hours = Math.floor((uptime % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((uptime % (60 * 60)) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const tabs = [
    { id: 'general', name: 'Általános', icon: Settings },
    { id: 'security', name: 'Biztonság', icon: Shield },
    { id: 'system', name: 'Rendszer Info', icon: Info },
    { id: 'backup', name: 'Biztonsági Mentés', icon: Database }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Rendszer Beállítások</h2>
        <p className="text-gray-600">Alkalmazás konfigurációja és rendszer információk</p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center">
          <Check className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg">
        {activeTab === 'general' && (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">Általános Beállítások</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alkalmazás neve
                  </label>
                  <input
                    type="text"
                    value={settings.appName}
                    onChange={(e) => handleInputChange('appName', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cég neve
                  </label>
                  <input
                    type="text"
                    value={settings.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum fájlméret (MB)
                  </label>
                  <input
                    type="number"
                    value={settings.maxFileSize}
                    onChange={(e) => handleInputChange('maxFileSize', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Engedélyezett fájltípusok
                  </label>
                  <input
                    type="text"
                    value={settings.allowedFileTypes}
                    onChange={(e) => handleInputChange('allowedFileTypes', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.enableDebugMode}
                    onChange={() => handleCheckboxChange('enableDebugMode')}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Debug mód engedélyezése</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={() => handleCheckboxChange('maintenanceMode')}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Karbantartási mód</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">Biztonsági Beállítások</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Munkamenet időtúllépés (perc)
                  </label>
                  <input
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => handleInputChange('sessionTimeout', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum bejelentkezési kísérlet
                  </label>
                  <input
                    type="number"
                    value={settings.maxLoginAttempts}
                    onChange={(e) => handleInputChange('maxLoginAttempts', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Figyelem</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      A biztonsági beállítások megváltoztatása hatással lehet a rendszer működésére. 
                      Győződj meg róla, hogy a változtatások előtt biztonsági mentést készítettél.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">Rendszer Információk</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Server className="h-5 w-5 text-blue-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">Verzió</h4>
                </div>
                <p className="text-lg font-semibold text-gray-900 mt-1">{systemInfo.version}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Server className="h-5 w-5 text-green-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">Üzemidő</h4>
                </div>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {systemInfo.uptime ? formatUptime(systemInfo.uptime) : 'N/A'}
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Database className="h-5 w-5 text-purple-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">Memória használat</h4>
                </div>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {systemInfo.memoryUsage ? formatBytes(systemInfo.memoryUsage) : 'N/A'}
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Database className="h-5 w-5 text-orange-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">Felhasználók</h4>
                </div>
                <p className="text-lg font-semibold text-gray-900 mt-1">{systemInfo.totalUsers}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Database className="h-5 w-5 text-red-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">Fájlok</h4>
                </div>
                <p className="text-lg font-semibold text-gray-900 mt-1">{systemInfo.totalFiles}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Database className="h-5 w-5 text-indigo-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">Megjegyzések</h4>
                </div>
                <p className="text-lg font-semibold text-gray-900 mt-1">{systemInfo.totalComments}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'backup' && (
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">Biztonsági Mentés</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mentési gyakoriság (óra)
                  </label>
                  <input
                    type="number"
                    value={settings.backupInterval}
                    onChange={(e) => handleInputChange('backupInterval', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utolsó mentés
                  </label>
                  <input
                    type="text"
                    value={systemInfo.lastBackup || 'Nincs adat'}
                    disabled
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.autoBackup}
                    onChange={() => handleCheckboxChange('autoBackup')}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Automatikus biztonsági mentés</span>
                </label>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                  <Info className="h-5 w-5 text-blue-400 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">Információ</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      A biztonsági mentés tartalmazza a felhasználói adatokat, jogosultságokat, 
                      megjegyzéseket és rendszer beállításokat. A PDF fájlok külön mentendők.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
          ) : (
            <Save className="h-5 w-5 mr-2" />
          )}
          {saving ? 'Mentés...' : 'Beállítások Mentése'}
        </button>
      </div>
    </div>
  );
};

export default SystemSettings; 